import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Modal } from '@unified-checkout/design-system';
import { Xmark } from '@unified-checkout/design-system/icons/index';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { IInstantSet } from '@app/dashboard/types/innerLayout/services/instant-set';
import { MAX_NOTIFICATION_WIDTH, NOTIFICATION_TIMEOUT } from '@app/dashboard/constants/transactions';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { notify } from '@app/dashboard/utils/notifier';
import { getMerchantClass, changeInstantSet } from '@app/dashboard/services/inner_layout/services/instant-set';
import style from './instant-set.module.scss';

export const InstantSetModal = ({
    isInstantSetOpen,
    setisInstantSetOpen,
    isInstantSetEnabled,
    setIsInstantSetEnabled
}: IInstantSet) => {
    const { t } = useTranslation();

    useEffect(() => {
        mutateGetMerchantClass();
    }, [])

    const {
        mutate: mutateGetMerchantClass,
    } = useMutation({
        mutationKey: [QUERY_KEYS.GET_MERCHANT_CLASS],
        mutationFn: () => getMerchantClass(),
        onSuccess: (resp) => {
            const isKeyAccount = resp?.data?.message !== "Key Account";
            { isKeyAccount ? setIsInstantSetEnabled(false) : setIsInstantSetEnabled(true); };
        },
        onError: (error: any) => {
            const errMessage = error.response?.data?.message ?? t('innerLayout.servicesPage.modals.instantSet.failed');
            notify.error(errMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, icon: Xmark, timeout: NOTIFICATION_TIMEOUT });
        }
    });

    const {
        mutate: mutateChangeInstantSet,
        isPending,
    } = useMutation({
        mutationKey: [QUERY_KEYS.CHANGE_INSTANT_SET],
        mutationFn: (isSubscribeAction: boolean) => {
            return changeInstantSet(isSubscribeAction);
        },
        onSuccess: (_, isSubscribeAction) => {
            setIsInstantSetEnabled(isSubscribeAction);
            notify.success(t('innerLayout.servicesPage.modals.instantSet.subscribeSuccess'), { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT });
            setisInstantSetOpen(false);
        },
        onError: (error: any) => {
            const errMessage = error.response?.data?.message ?? t('innerLayout.servicesPage.modals.instantSet.failed');
            notify.error(errMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT, icon: Xmark });
            setisInstantSetOpen(false);
        }
    })

    const handleToggleInstantSet = (isSubscribeAction: boolean) => {
        mutateChangeInstantSet(isSubscribeAction);
    };

    const cancelBtnProps: IButton = {
        text: t('innerLayout.servicesPage.modals.btns.cancel'),
        size: "lg",
        variant: "outlined",
        color: "secondary",
        disabled: isPending,
        onClickHandler: () => setisInstantSetOpen(false)
    };

    const actionButton: IButton = isInstantSetEnabled ? {
        text: t('innerLayout.servicesPage.modals.btns.unsubscrbe'),
        size: "lg",
        variant: "contained",
        color: "danger",
        loading: isPending,
        disabled: isPending,
        onClickHandler: () => handleToggleInstantSet(false)
    } : {
        text: t('innerLayout.servicesPage.modals.btns.subscribe'),
        size: "lg",
        variant: "contained",
        color: "primary",
        loading: isPending,
        disabled: isPending,
        // onClickHandler: () => handleToggleInstantSet(true)
    };

    const title = t('innerLayout.servicesPage.modals.instantSet.subscribeTitle');
    
    const description = t('innerLayout.servicesPage.modals.instantSet.subscribeDesc');

    const btns = [actionButton, cancelBtnProps];

    return (
        <>
            <Modal
                state="default"
                open={isInstantSetOpen}
                position="center"
                onCloseHandler={() => setisInstantSetOpen(false)}
                title={title}
                supportingText={description}
                btnsProps={btns}
            >
                <div className={style["benefits"]}>
                    <div>
                        {t('innerLayout.servicesPage.modals.instantSet.benefits')}
                    </div>
                    <ul>
                        <li>
                            {t('innerLayout.servicesPage.modals.instantSet.benefit_1')}

                        </li>
                        <li>
                            {t('innerLayout.servicesPage.modals.instantSet.benefit_2')}
                        </li>
                    </ul>
                </div>
            </Modal>
        </>
    );
}